import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  isChunkError: boolean;
}

const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes('dynamically imported module') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * Zeigt Render-Fehler sichtbar statt weißer Seite (hilft bei lokalem & Docker-Dev).
 * ChunkLoadError (Stale-Deployment) löst einmaligen Auto-Reload aus.
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error)) {
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
        return { error: null, isChunkError: false };
      }
      return { error, isChunkError: true };
    }
    return { error, isChunkError: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('RootErrorBoundary:', error, info.componentStack);
  }

  componentDidMount() {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  }

  render() {
    const { error, isChunkError } = this.state;

    if (error) {
      if (isChunkError) {
        return (
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#121212',
              color: '#fff',
              fontFamily: 'system-ui, sans-serif',
              gap: 16,
            }}
          >
            <h1 style={{ color: '#ff6600', margin: 0 }}>Seite veraltet</h1>
            <p style={{ color: '#ccc', margin: 0, textAlign: 'center', maxWidth: 400 }}>
              Die App wurde aktualisiert. Bitte lade die Seite neu, um die aktuelle Version zu laden.
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem(CHUNK_RELOAD_KEY);
                window.location.reload();
              }}
              style={{
                marginTop: 8,
                padding: '10px 24px',
                background: '#ff6600',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 15,
                cursor: 'pointer',
              }}
            >
              Seite neu laden
            </button>
          </div>
        );
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            padding: 24,
            background: '#121212',
            color: '#fff',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ color: '#ff6600' }}>Fehler beim Rendern</h1>
          <p style={{ color: '#ccc' }}>
            Die App ist abgestürzt. Details siehe unten oder Browser-Konsole (F12).
          </p>
          <pre
            style={{
              marginTop: 16,
              padding: 16,
              background: '#1a1a1a',
              borderRadius: 8,
              overflow: 'auto',
              fontSize: 13,
              whiteSpace: 'pre-wrap',
            }}
          >
            {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}
